/**
 * Centralized ticket availability status logic
 * Used across the application for consistent ticket status display
 */

export type TicketAvailabilityStatus =
	| `sold-out`
	| `few-left`
	| `selling-fast`
	| `available`

export interface TicketAvailabilityResult {
	status: TicketAvailabilityStatus
	text: string
	color: string
	colorClass: string
}

export interface TicketAvailabilityInput {
	availableNow: number
	totalAvailable: number
}

/**
 * Calculate ticket availability status using hybrid thresholds
 *
 * Business Rules (Option B - Hybrid):
 * - Sold Out: 0 tickets available
 * - Few left: ≥90% sold OR <10 tickets remaining (whichever comes first)
 * - Selling fast: 50-89% sold
 * - Available: <50% sold
 *
 * @param input - Object with availableNow and totalAvailable counts
 * @returns Ticket availability status with display properties
 */
export function getTicketAvailabilityStatus(
	input: TicketAvailabilityInput,
): TicketAvailabilityResult {
	const { availableNow, totalAvailable } = input

	// Sold Out: No tickets available
	if (availableNow === 0) {
		return {
			status: `sold-out`,
			text: `Sold Out`,
			color: `red`,
			colorClass: `text-red-600`,
		}
	}

	// Calculate utilization percentage
	const utilizationPercentage =
		totalAvailable > 0
			? ((totalAvailable - availableNow) / totalAvailable) * 100
			: 0

	// Few left: ≥90% sold OR <10 tickets remaining (hybrid approach)
	if (utilizationPercentage >= 90 || availableNow < 10) {
		return {
			status: `few-left`,
			text: `Few Left`,
			color: `orange`,
			colorClass: `text-orange-600`,
		}
	}

	// Selling fast: 50-89% sold
	if (utilizationPercentage >= 50) {
		return {
			status: `selling-fast`,
			text: `Selling Fast`,
			color: `yellow`,
			colorClass: `text-yellow-600`,
		}
	}

	// Available: <50% sold
	return {
		status: `available`,
		text: `Available`,
		color: `green`,
		colorClass: `text-green-600`,
	}
}
